import { useMemo, useState } from "react";
import {
  Check,
  MessageSquare,
  MoreVertical,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { Button, Input } from "@lyder-talk/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MascotConfirmDialog } from "@/components/common/MascotConfirmDialog";
import {
  SidebarEmptyState,
  SidebarHeader,
  SidebarPrimaryAction,
  SidebarRow,
  SidebarSearchField,
  SidebarSection,
  SidebarSkeleton,
} from "@/components/layout/SidebarShell";
import type { ConversationSummary } from "@/lib/ai-chat/types";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useTranslation } from "react-i18next";

type TimeSection = "Today" | "Yesterday" | "This week" | "This month" | "Older";

function getTimeSection(dateStr: string): TimeSection {
  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);
  const monthAgo = new Date(today);
  monthAgo.setMonth(monthAgo.getMonth() - 1);

  if (date >= today) return "Today";
  if (date >= yesterday) return "Yesterday";
  if (date >= weekAgo) return "This week";
  if (date >= monthAgo) return "This month";
  return "Older";
}

function groupConversationsByTime(
  conversations: ConversationSummary[]
): Array<{ section: TimeSection; items: ConversationSummary[] }> {
  const groups: Record<TimeSection, ConversationSummary[]> = {
    Today: [],
    Yesterday: [],
    "This week": [],
    "This month": [],
    Older: [],
  };

  for (const conv of conversations) {
    const section = getTimeSection(conv.updated_at || conv.created_at);
    groups[section].push(conv);
  }

  return (Object.entries(groups) as Array<[TimeSection, ConversationSummary[]]>)
    .filter(([, items]) => items.length > 0)
    .map(([section, items]) => ({ section, items }));
}

interface AIConversationListProps {
  conversations: ConversationSummary[];
  currentConversationId?: string;
  onSelectConversation: (id: string) => void;
  onNewChat: () => void;
  onEditConversation?: (id: string, newTitle: string) => void;
  onDeleteConversation?: (id: string) => void;
  loading?: boolean;
}

/**
 * AI conversation history sidebar content, built on the shared sidebar shell so
 * it reads identically to the practice sidebar (gradient "New" action, search,
 * collapsible sections, hover-revealed row actions). Time-grouped.
 * Embedded by AppShell, which supplies the panel chrome + mobile Sheet.
 */
export function AIConversationList({
  conversations,
  currentConversationId,
  onSelectConversation,
  onNewChat,
  onEditConversation,
  onDeleteConversation,
  loading,
}: AIConversationListProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const filtered = conversations.filter((conv) =>
    conv.title.toLowerCase().includes(debouncedSearchQuery.trim().toLowerCase())
  );
  const grouped = useMemo(() => groupConversationsByTime(filtered), [filtered]);

  const startEdit = (conv: ConversationSummary) => {
    setEditingId(conv.id);
    setEditingTitle(conv.title);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingTitle("");
  };

  const saveEdit = (id: string) => {
    if (editingTitle.trim()) onEditConversation?.(id, editingTitle.trim());
    cancelEdit();
  };

  const hasRowActions = !!(onEditConversation || onDeleteConversation);

  return (
    <div className="flex flex-col h-full">
      <SidebarHeader title={t("pages.myTopics.sidebar.conversationsTitle", "Conversations")} />

      <SidebarPrimaryAction icon={Plus} label={t("pages.myTopics.sidebar.newChat", "New Chat")} onClick={onNewChat} />

      <SidebarSearchField
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder={t("pages.myTopics.sidebar.searchConversations", "Search conversations...")}
      />

      <div className="min-w-0 flex-1 overflow-y-auto">
        {loading ? (
          <SidebarSkeleton />
        ) : filtered.length === 0 ? (
          <SidebarEmptyState
            icon={MessageSquare}
            message={debouncedSearchQuery.trim()
              ? t("pages.myTopics.sidebar.noConversationsFound", "No conversations found")
              : t("pages.myTopics.sidebar.noConversationsYet", "No conversations yet")}
          />
        ) : (
          grouped.map(({ section, items }) => (
            <SidebarSection
              key={section}
              title={t(`pages.myTopics.sidebar.time${section.replace(/\s/g, "")}`, { defaultValue: section })}
              open={!collapsed[section]}
              onOpenChange={(open) =>
                setCollapsed((prev) => ({ ...prev, [section]: !open }))
              }
            >
              {items.map((conv) =>
                editingId === conv.id ? (
                  <div
                    key={conv.id}
                    className="flex items-center gap-1 px-2 py-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Input
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveEdit(conv.id);
                        if (e.key === "Escape") cancelEdit();
                      }}
                      className="h-7 text-sm flex-1 min-w-0"
                      autoFocus
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      onClick={() => saveEdit(conv.id)}
                    >
                      <Check className="w-3.5 h-3.5 text-green-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      onClick={cancelEdit}
                    >
                      <X className="w-3.5 h-3.5 text-muted-foreground" />
                    </Button>
                  </div>
                ) : (
                  <SidebarRow
                    key={conv.id}
                    active={currentConversationId === conv.id}
                    onClick={() => onSelectConversation(conv.id)}
                    actions={
                      hasRowActions ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 hover:bg-primary/15 data-[state=open]:bg-primary/15"
                              title={t("pages.myTopics.sidebar.moreOptions", "More options")}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreVertical className="w-3.5 h-3.5 text-muted-foreground hover:text-primary" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" side="right" className="min-w-[140px]">
                            {onEditConversation && (
                              <DropdownMenuItem
                                className="cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEdit(conv);
                                }}
                              >
                                <Pencil className="w-3.5 h-3.5 mr-2" />
                                Rename
                              </DropdownMenuItem>
                            )}
                            {onDeleteConversation && (
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteId(conv.id);
                                }}
                              >
                                <Trash2 className="w-3.5 h-3.5 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : undefined
                    }
                  >
                    <span className="block truncate text-sm">{conv.title}</span>
                  </SidebarRow>
                )
              )}
            </SidebarSection>
          ))
        )}
      </div>

      <MascotConfirmDialog
        open={!!deleteId}
        onOpenChange={(open) => {
          if (!open) setDeleteId(null);
        }}
        onConfirm={() => deleteId && onDeleteConversation?.(deleteId)}
        title={t("pages.myTopics.sidebar.deleteConversationTitle", "Delete conversation?")}
        description={t("pages.myTopics.sidebar.deleteConversationDescription", "This action cannot be undone. The conversation will be permanently deleted.")}
        confirmLabel={t("pages.myTopics.delete", "Delete")}
        cancelLabel={t("pages.myTopics.sidebar.cancel", "Cancel")}
        destructive
      />
    </div>
  );
}
